import { firestore } from '../firebase/config';
import { collection, addDoc, Timestamp } from 'firebase/firestore';

/**
 * Registra alterações em documentos para fins de auditoria.
 * Compara dois objetos (antigo e novo) e salva as diferenças.
 * 
 * @param {string} visitaId - ID do documento modificado
 * @param {object} dadosAntigos - Objeto com os dados ANTES da alteração
 * @param {object} dadosNovos - Objeto com os dados DEPOIS da alteração
 * @param {object} usuarioResponsavel - Objeto do usuário logado (deve conter uid e email/nome)
 * @param {string} colecao - Nome da coleção onde ocorreu a alteração (padrão: 'relatoriosDiarios_FAD1')
 */
export const registrarAuditoria = async (visitaId, dadosAntigos, dadosNovos, usuarioResponsavel, colecao = 'relatoriosDiarios_FAD1') => {
    try {
        if (!usuarioResponsavel) {
            console.warn("Auditoria: Usuário responsável não fornecido.");
            return;
        }

        const alteracoes = compararObjetos(dadosAntigos, dadosNovos);

        if (alteracoes.length === 0) {
            console.log("Auditoria: Nenhuma alteração detectada para registrar.");
            return;
        }

        const log = {
            visitaId,
            colecaoAlvo: colecao,
            dataAlteracao: Timestamp.now(),
            responsavel: {
                uid: usuarioResponsavel.uid,
                identificador: usuarioResponsavel.nome || usuarioResponsavel.email || 'Desconhecido',
                perfil: usuarioResponsavel.perfil || 'N/A'
            },
            contexto: {
                idVisita: dadosAntigos.idVisita || dadosNovos.idVisita || 'N/A',
                logradouro: dadosAntigos.logradouro || dadosNovos.logradouro || 'N/A',
                numero: dadosAntigos.numeroImovel || dadosNovos.numeroImovel || 'S/N',
                localidade: dadosAntigos.localidade || dadosNovos.localidade || ''
            },
            detalhes: alteracoes
        };

        await addDoc(collection(firestore, 'auditoria_alteracoes'), log);
        console.log("Auditoria: Log registrado com sucesso.", log);

    } catch (error) {
        console.error("Auditoria: Erro ao registrar log.", error);
        // Não lançamos o erro para não impedir o fluxo principal de salvamento
    }
};

/**
 * Compara dois objetos e retorna um array de diferenças.
 * Ignora campos que não são relevantes para auditoria (como Timestamps internos se não mudaram logicamente).
 */
const compararObjetos = (antigo, novo, prefixo = '') => {
    let diffs = [];

    // Campos a ignorar na comparação direta
    const camposIgnorados = ['id', 'dataAtividade', 'createdAt', 'updatedAt'];

    const allKeys = new Set([...Object.keys(antigo || {}), ...Object.keys(novo || {})]);

    allKeys.forEach(key => {
        if (camposIgnorados.includes(key)) return;

        const valAntigo = antigo ? antigo[key] : undefined;
        const valNovo = novo ? novo[key] : undefined;

        // Se ambos são objetos (e não arrays/dates/null), recursão
        if (ehObjeto(valAntigo) && ehObjeto(valNovo)) {
            const subDiffs = compararObjetos(valAntigo, valNovo, `${prefixo}${key}.`);
            diffs = [...diffs, ...subDiffs];
            return;
        }

        // Comparação de Arrays (Simplificada: compara como JSON string para detectar mudança)
        if (Array.isArray(valAntigo) || Array.isArray(valNovo)) {
            if (JSON.stringify(valAntigo) !== JSON.stringify(valNovo)) {
                diffs.push({
                    campo: `${prefixo}${key}`,
                    anterior: valAntigo ? JSON.stringify(valAntigo) : 'N/A',
                    novo: valNovo ? JSON.stringify(valNovo) : 'N/A'
                });
            }
            return;
        }

        // Comparação de Valores Primitivos
        // Normaliza undefined/null para comparação justa
        const v1 = valAntigo === undefined || valAntigo === null ? '' : valAntigo;
        const v2 = valNovo === undefined || valNovo === null ? '' : valNovo;

        if (String(v1) !== String(v2)) {
            diffs.push({
                campo: `${prefixo}${key}`,
                anterior: String(v1) || '(vazio)',
                novo: String(v2) || '(vazio)'
            });
        }
    });

    return diffs;
};

const ehObjeto = (item) => {
    return (item && typeof item === 'object' && !Array.isArray(item));
};
